import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <svg className="w-8 h-8" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M50 5L93.3 27.5V72.5L50 95L6.7 72.5V27.5L50 5Z" fill="#DC2626"/>
              <path d="M35 35H50C55.5 35 60 39.5 60 45C60 50.5 55.5 55 50 55H42V65H35V35ZM42 48H48C50.2 48 52 46.2 52 44C52 41.8 50.2 40 48 40H42V48Z" fill="white"/>
              <path d="M50 55L65 65H55L42 55H50Z" fill="white"/>
            </svg>
            <span className="font-display font-bold text-lg tracking-tight text-white">Build with Redline</span>
          </Link>
          <Link 
            to="/" 
            className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-32 pb-24 px-6">
        <div className="max-w-3xl mx-auto">
          <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-4">
            Privacy Policy
          </h1>
          <p className="text-gray-500 mb-12">Last updated: March 2026</p>

          <div className="prose prose-invert prose-gray max-w-none space-y-8">
            
            <section>
              <h2 className="font-display text-2xl font-bold text-white mb-4">Introduction</h2>
              <p className="text-gray-400 leading-relaxed">
                Build with Redline ("we," "our," or "us") respects your privacy and is committed to protecting your personal data. This privacy policy explains how we collect, use, disclose, and safeguard your information when you visit our website buildwithredline.com or engage with our services.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-white mb-4">Information We Collect</h2>
              <p className="text-gray-400 leading-relaxed mb-4">
                We may collect the following types of information:
              </p>
              <ul className="list-disc list-inside text-gray-400 space-y-2">
                <li><strong className="text-gray-300">Contact Information:</strong> Name, email address, phone number, and company name when you fill out our contact form or book a consultation.</li>
                <li><strong className="text-gray-300">Communications:</strong> Records of correspondence if you contact us directly.</li>
                <li><strong className="text-gray-300">Usage Data:</strong> Information about how you interact with our website, including pages visited, time spent, and referring sources.</li>
                <li><strong className="text-gray-300">Technical Data:</strong> IP address, browser type, device information, and operating system.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-white mb-4">How We Use Your Information</h2>
              <p className="text-gray-400 leading-relaxed mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc list-inside text-gray-400 space-y-2">
                <li>Respond to your inquiries and provide requested services</li>
                <li>Schedule and conduct consultations</li>
                <li>Send relevant communications about our services</li>
                <li>Improve our website and user experience</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-white mb-4">Information Sharing</h2>
              <p className="text-gray-400 leading-relaxed">
                We do not sell, trade, or rent your personal information to third parties. We may share your information with trusted service providers who assist us in operating our website and conducting our business (such as email services and scheduling tools), provided they agree to keep your information confidential.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-white mb-4">Data Security</h2>
              <p className="text-gray-400 leading-relaxed">
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-white mb-4">Cookies</h2>
              <p className="text-gray-400 leading-relaxed">
                Our website may use cookies and similar tracking technologies to enhance your experience. You can set your browser to refuse cookies, but some features of our website may not function properly as a result.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-white mb-4">Third-Party Links</h2>
              <p className="text-gray-400 leading-relaxed">
                Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-white mb-4">Your Rights</h2>
              <p className="text-gray-400 leading-relaxed mb-4">
                Depending on your location, you may have certain rights regarding your personal data, including:
              </p>
              <ul className="list-disc list-inside text-gray-400 space-y-2">
                <li>The right to access your personal information</li>
                <li>The right to correct inaccurate data</li>
                <li>The right to request deletion of your data</li>
                <li>The right to opt out of marketing communications</li>
              </ul>
              <p className="text-gray-400 leading-relaxed mt-4">
                To exercise any of these rights, please contact us at hello@buildwithredline.com.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-white mb-4">Changes to This Policy</h2>
              <p className="text-gray-400 leading-relaxed">
                We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date. We encourage you to review this policy periodically.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-white mb-4">Contact Us</h2>
              <p className="text-gray-400 leading-relaxed">
                If you have any questions about this Privacy Policy, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-white/5 border border-white/10 rounded-xl">
                <p className="text-white font-medium">Build with Redline</p>
                <p className="text-gray-400">Email: mike@redlinemonitoring.com</p>
              </div>
            </section>

          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 bg-black border-t border-white/10">
        <div className="max-w-4xl mx-auto text-center text-sm text-gray-500">
          © 2026 Build with Redline
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
